import classNames from 'classnames';
import PropTypes from 'prop-types';

import { analytics, getClickCtaDetail } from '@/utils/analytics';
import { isDDEnv, isWHEnv } from '@/utils/helper';

import Text from '@/atoms/Text/Text';
import CtfCta from '@/molecules/CTA/index';

import { TEXT_BASE_CLASS } from '@/atoms/Text/Text.constants';
import {
  CtfSectionHeaderHeadingSize,
  CtfSectionHeaderType,
  CtfSectionHeaderPositions,
} from '@/utils/constants';
import URLReplacer from '@/organisms/URLReplacer/URLReplacer';

export function SectionHeaderComponent({
  alignment,
  headingWeight,
  sectionHeadingSize,
  customHeadingSize = false,
  cta,
  subtext,
  title,
  titleColor,
  titleStyle,
  subtextColor,
  type,
  columnLayout,
  compactText,
  hideLine,
  smallTextTopLeft = '',
  subTextFontSize,
  subTextCenter = false,
  color = 'black',
  removeMargin = false,
  program,
}) {
  const isDD = isDDEnv();
  const titleWrapperClass = getTitleWrapperClass({
    alignment,
    type,
    columnLayout,
  });
  const titleClass = getTitleClass({
    alignment,
    type,
    color: titleColor,
    customHeadingSize,
    headingWeight,
    titleStyle,
    sectionHeadingSize,
    isDD,
  });
  const subtextClass = getSubtextClass({
    type,
    columnLayout,
    compactText,
    sectionHeadingSize,
    subtextColor,
  });

  return (
    <div
      className={
        isDD
          ? `inline-flex items-start  flex-row ${
              alignment === CtfSectionHeaderPositions.LEFT
                ? 'md:justify-start'
                : ''
            } ${
              alignment === CtfSectionHeaderPositions.CENTER
                ? 'md:justify-center dd-en:text-center dd-kr:text-center'
                : ''
            } ${
              alignment === CtfSectionHeaderPositions.RIGHT
                ? 'md:justify-end'
                : ''
            } ${
              (type === CtfSectionHeaderType.PAGE ||
                type === CtfSectionHeaderType.PAGE_LARGE) &&
              alignment === CtfSectionHeaderPositions.CENTER
                ? 'text-center'
                : ''
            } ${
              (type === CtfSectionHeaderType.SECTION ||
                type === CtfSectionHeaderType.LEVEL_3) &&
              ''
            } ${`aaco-section-header--${type}`}`
          : `header-container  my-0 ${
              removeMargin ? '' : 'container mx-auto'
            } ${subtext ? ' pb-5' : ''}
      ${type === CtfSectionHeaderType.PAGE && 'pb-0'}`
      }
    >
      <div className={isDD ? titleWrapperClass : 'wh-section-header'}>
        <div className={isDD ? 'flex flex-row items-center align-middle' : ''}>
          {smallTextTopLeft && (
            <Text tag="caption" style={`text-${color} font-pro-mono`}>
              {smallTextTopLeft}
            </Text>
          )}
          <div className="flex flex-row items-center">
            <Text
              tag={headingWeight?.toLowerCase()}
              style={`${titleClass} dd-kr:flex dd-en:flex flex-row flex-wrap items-center align-middle md:gap-x-2.5 dd-en:gap-x-[18.8px] dd-kr:gap-x-[18.8px] dd-en:gap-y-[46px] dd-kr:gap-y-[46px] md:w-auto`}
              custom
            >
              {title}
              {!hideLine &&
                isDD &&
                (type === CtfSectionHeaderType.SECTION ||
                  (type === CtfSectionHeaderType.LEVEL_2 && !isDD)) && (
                  <span
                    className={`aaco-section-header__divider inline-block ${
                      CtfSectionHeaderType.LEVEL_2
                        ? 'w-[55px] md:ml-2.5 dd-kr:md:ml-0 dd-en:md:ml-0 dd-kr:w-[70px] dd-en:w-[70px] md:w-[75px]'
                        : 'w-[75px]'
                    } md:w-[150px] border-solid border-b-2 border-eucalyptus dd-en:border-actual-dd-gold dd-kr:border-actual-dd-gold align-middle`}
                  />
                )}
            </Text>
            {program && (
              <div
                className={classNames(
                  'h-[61px] w-[61px] ml-auto md:ml-[30px] md:h-[87.26px] md:w-[87.26px] rounded-full bg-millet',
                  {
                    'bg-millet': program === 'Pure',
                    'bg-sorghum': program === 'Cross',
                    '!bg-cooper-clover': program === 'Forage',
                  }
                )}
              />
            )}
          </div>
        </div>
        {/* divider line below */}
        {!isDD && (
          <div
            className={`aaco-section-header__divider w-full border-solid border-b-1 mt-4 border-${color}`}
          ></div>
        )}
        {subtext && (
          <Text
            tag={
              subTextFontSize === 'large'
                ? 'sub-header-heading'
                : subTextFontSize === 'body-large'
                ? 'body-large'
                : 'body'
            }
            style={
              subtextClass +
              ` ${
                subTextCenter ? '!text-center !my-[20px] !mx-auto' : ''
              } max-w-[650px]  block`
            }
          >
            <URLReplacer text={subtext} />
          </Text>
        )}
        {cta}
      </div>
    </div>
  );
}

SectionHeaderComponent.defaultProps = {
  alignment: CtfSectionHeaderPositions.LEFT,
  sectionHeadingSize: CtfSectionHeaderHeadingSize.H2,
};

function SectionHeader({
  fields,
  entries,
  columnLayout,
  compactText,
  hasBackground,
}) {
  const {
    alignment,
    callToAction,
    hideLine,
    headingWeight,
    sectionHeadingSize,
    id,
    subtext,
    title,
    style: type,
    color,
    subtextColor,
    smallTextTopLeft,
    subTextFontSize,
    subTextCenter,
    backgroundColor,
    removeMargin,
    program,
  } = fields;
  const titleColorCls = color ? `text-${color}` : '';
  const subtextColorCls = subtextColor ? `text-${subtextColor}` : '';
  const isWH = isWHEnv();
  return (
    <div
      className={`aaco-section-header w-auto flex ${isWH ? 'py-9' : ''}
      ${backgroundColor !== undefined && `bg-${backgroundColor}`}
      ${type === CtfSectionHeaderType.PAGE && 'py-6 md:pb-12'}
    `}
      id={id}
      aria-label={id}
    >
      <SectionHeaderComponent
        alignment={alignment}
        cta={
          callToAction && (
            <div className="aaco-section-header__cta dd-en:mt-[18.3px] dd-kr:mt-[18.3px] mt-[15px]">
              <CtfCta
                field={callToAction}
                entries={entries}
                invert={hasBackground}
                onClick={(e, { localUrl, url, name }) =>
                  analytics({
                    window,
                    document,
                    detail: getClickCtaDetail(e, { localUrl, url, name }),
                  })
                }
              />
            </div>
          )
        }
        sectionHeadingSize={sectionHeadingSize}
        headingWeight={headingWeight}
        subtext={subtext}
        title={title}
        titleColor={titleColorCls}
        subtextColor={subtextColorCls}
        type={type}
        columnLayout={columnLayout}
        compactText={compactText}
        hideLine={hideLine}
        color={color}
        smallTextTopLeft={smallTextTopLeft}
        subTextFontSize={subTextFontSize}
        subTextCenter={subTextCenter}
        entries={entries}
        removeMargin={removeMargin}
        program={program}
      />
    </div>
  );
}

SectionHeader.propTypes = {
  fields: PropTypes.shape({
    // only for style = center, left | center | right
    alignment: PropTypes.string,
    id: PropTypes.string,
    // only used on section, default H2
    sectionHeadingSize: PropTypes.string,
    // H1-H6 consistent style, only tags change
    headingWeight: PropTypes.string,
    subtext: PropTypes.string,
    // page | section
    style: PropTypes.string,
    title: PropTypes.string,
  }),
};

export default SectionHeader;

const getTitleWrapperClass = ({ alignment, type, columnLayout }) =>
  classNames(
    'aaco-section-header__title-wrapper flex flex-col mb-20 dd-en:mb-10 dd-kr:mb-10 overflow-x-auto',
    'overflow-y-hidden',
    {
      'md:pb-2.5': isWHEnv() && CtfSectionHeaderType.LEVEL_2,
      'w-full md:w-2/3 2xl:w-1/2 ':
        type === CtfSectionHeaderType.SECTION && !columnLayout,
      'w-full z-10': columnLayout,
      'w-full pt-[28px] md:pt-9 md:dd-kr:pt-20 md:dd-en:pt-20 md:dd-en:mb-[60px] md:dd-kr:mb-[60px]':
        type === CtfSectionHeaderType.PAGE,
    }
  );

const getTitleClass = ({
  alignment,
  type,
  color,
  customHeadingSize,
  sectionHeadingSize,
  titleStyle,
  isDD,
}) => {
  const colorStyle = color?.length
    ? color
    : 'text-black dd-en:text-progressive-blue dd-kr:text-progressive-blue';
  let headingText = '';
  if (
    (customHeadingSize || type === CtfSectionHeaderType.SECTION) &&
    type === CtfSectionHeaderType.SECTION &&
    sectionHeadingSize
  ) {
    headingText = TEXT_BASE_CLASS[sectionHeadingSize];
  } else if (type === CtfSectionHeaderType.PAGE && !isDD) {
    headingText =
      'font-heading font-h3-mobile md:font-h3 text-h3-mobile md:text-h3';
  } else if (type === CtfSectionHeaderType.PAGE && isDD) {
    headingText = TEXT_BASE_CLASS.h2;
  } else if (type === CtfSectionHeaderType.LEVEL_2) {
    headingText = TEXT_BASE_CLASS.h2;
  } else if (type === CtfSectionHeaderType.LEVEL_3) {
    headingText = TEXT_BASE_CLASS.h5;
  } else if (type === CtfSectionHeaderType.PAGE_LARGE) {
    headingText = 'text-page-large';
  } else if (type === CtfSectionHeaderType.H_4) {
    headingText = TEXT_BASE_CLASS.h4;
  }

  if (
    alignment &&
    (type === CtfSectionHeaderType.PAGE ||
      type === CtfSectionHeaderType.PAGE_LARGE)
  ) {
    if (alignment === CtfSectionHeaderPositions.CENTER) {
      headingText += ' justify-center';
    } else if (alignment === CtfSectionHeaderPositions.LEFT) {
      headingText += ' justify-left';
    } else if (alignment === CtfSectionHeaderPositions.RIGHT) {
      headingText += ' justify-right';
    }
  }

  if (type === CtfSectionHeaderType.SECTION) {
    headingText += ' w-max';
  }

  return classNames(
    'aaco-section-header__title w-full',
    colorStyle,
    titleStyle,
    'whitespace-pre-line',
    headingText
  );
};

const getSubtextClass = ({
  type,
  subtextColor,
  columnLayout,
  compactText,
  sectionHeadingSize,
}) =>
  classNames(
    `aaco-section-header__subtext flex-wrap whitespace-pre-wrap mt-3 ${subtextColor}`,
    {
      'text-black': !subtextColor,
      '!mt-5 dd-kr:mt-[22px] dd-en:mt-[22px] md:!mt-12 md:dd-kr:mt-1.5 md:dd-en:mt-1.5':
        type === CtfSectionHeaderType.PAGE ||
        type === CtfSectionHeaderType.PAGE_LARGE,
      'mt-[10px] mb-[22px]':
        type === CtfSectionHeaderType.SECTION &&
        sectionHeadingSize !== CtfSectionHeaderHeadingSize.H1,
      'mt-[10px] mb-[22px]':
        type === CtfSectionHeaderType.SECTION &&
        sectionHeadingSize === CtfSectionHeaderHeadingSize.H1,
      'w-full md:w-2/3 2xl:w-1/2': compactText,
    }
  );
